import type { NavItemType } from 'types/menu'

import {
  AccountBookOutlined,
  AimOutlined,
  AuditOutlined,
  BarChartOutlined,
  BuildOutlined,
  CalendarOutlined,
  ClusterOutlined,
  CompassOutlined,
  ContactsOutlined,
  ContainerOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  FileSyncOutlined,
  FileTextOutlined,
  GiftOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  RadarChartOutlined,
  RocketOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UploadOutlined,
} from '@ant-design/icons'

import { FormattedMessage } from 'react-intl'
import { Permissions } from 'types/permissions'

const pages: NavItemType = {
  id: 'group-pages',
  type: 'group',
  children: [
    {
      id: 'dashboard',
      title: <FormattedMessage id="dashboard" />,
      type: 'item',
      url: '/dashboard',
      icon: DashboardOutlined,
    },
    {
      id: 'comp-plan',
      title: <FormattedMessage id="comp-plan" />,
      type: 'item',
      url: '/compplan',
      icon: CompassOutlined,
      subscriptionTierFeature: ['INCENTIVE.ALL'],
    },
    {
      id: 'organization',
      title: <FormattedMessage id="organization" />,
      type: 'item',
      url: '/organization',
      icon: ClusterOutlined,
      permissions: [Permissions.MANAGER_DASHBOARD_READ],
    },
    {
      id: 'admin-templates',
      title: <FormattedMessage id="admin-templates" />,
      type: 'item',
      url: '/admin/templates',
      icon: BuildOutlined,
      permissions: [Permissions.TEMPLATES_WRITE],
      subscriptionTierFeature: ['INCENTIVE.ALL'],
    },
    {
      id: 'components',
      title: <FormattedMessage id="admin-comp" />,
      type: 'collapse',
      icon: RadarChartOutlined,
      permissions: [Permissions.COMPONENTS_WRITE],
      subscriptionTierFeature: ['INCENTIVE.ALL'],
      children: [
        {
          id: 'components-list',
          title: <FormattedMessage id="admin-comp-manage" />,
          type: 'item',
          url: '/admin/components/manage',
          permissions: [Permissions.TARGETS_WRITE],
          icon: RadarChartOutlined,
        },
        {
          id: 'targets',
          title: <FormattedMessage id="admin-targets" />,
          type: 'item',
          url: '/admin/components/targets',
          permissions: [Permissions.TARGETS_WRITE],
          icon: AimOutlined,
        },
        {
          id: 'metrics',
          title: <FormattedMessage id="admin-metrics" />,
          type: 'item',
          url: '/admin/components/actuals',
          permissions: [Permissions.METRICS_WRITE],
          icon: BarChartOutlined,
        },
      ],
    },
    {
      id: 'admin-period-plans',
      title: <FormattedMessage id="admin-period-plans" />,
      type: 'item',
      url: '/admin/period-plans',
      icon: FileSyncOutlined,
      permissions: [Permissions.UNPUBLISHED_MANAGER_READ],
      subscriptionTierFeature: ['INCENTIVE.ALL'],
    },
    {
      id: 'admin-approvals',
      title: <FormattedMessage id="admin-approvals" />,
      type: 'item',
      url: '/admin/approvals',
      icon: AuditOutlined,
      permissions: [Permissions.APPROVALS_WRITE],
      subscriptionTierFeature: ['INCENTIVE.ALL'],
    },
    {
      id: 'rewards',
      title: <FormattedMessage id="rewards" />,
      type: 'collapse',
      icon: GiftOutlined,
      permissions: [Permissions.REWARDS_ADMIN],
      subscriptionTierFeature: ['REWARDS.ALL'],
      children: [
        {
          id: 'rewards-manage',
          title: <FormattedMessage id="rewards-manage" />,
          type: 'item',
          url: '/rewards/manage',
          permissions: [Permissions.REWARDS_ADMIN],
        },
        {
          id: 'rewards-packages',
          title: <FormattedMessage id="rewards-packages" />,
          type: 'item',
          url: '/rewards/packages',
          permissions: [Permissions.REWARDS_ADMIN],
        },
        {
          id: 'rewards-assign',
          title: <FormattedMessage id="rewards-assign" />,
          type: 'item',
          url: '/rewards/assign',
          permissions: [Permissions.REWARDS_ADMIN],
        },
      ],
    },
    {
      id: 'admin-accruals-group',
      title: <FormattedMessage id="admin-accruals-group" />,
      type: 'collapse',
      icon: CalendarOutlined,
      permissions: [Permissions.ACCRUALS_WRITE],
      subscriptionTierFeature: ['INCENTIVE.ALL'],
      children: [
        {
          id: 'admin-accruals',
          title: <FormattedMessage id="admin-accruals" />,
          type: 'item',
          url: '/admin/accruals',
          permissions: [Permissions.ACCRUALS_WRITE],
          icon: FieldTimeOutlined,
        },
        {
          id: 'admin-accruals-ledger',
          title: <FormattedMessage id="admin-accruals-ledger" />,
          type: 'item',
          url: '/admin/accrual-ledger',
          permissions: [Permissions.ACCRUALS_WRITE],
          icon: AccountBookOutlined,
        },
        {
          id: 'admin-close-periods',
          title: <FormattedMessage id="admin-close-periods" />,
          type: 'item',
          url: '/admin/close-periods',
          permissions: [Permissions.ACCRUALS_WRITE],
          icon: CalendarOutlined,
        },
      ],
    },
    {
      id: 'reporting',
      title: <FormattedMessage id="reporting" />,
      type: 'collapse',
      icon: BarChartOutlined,
      permissions: [Permissions.REPORTS_READ],
      children: [
        {
          id: 'forms',
          title: <FormattedMessage id="forms" />,
          type: 'item',
          url: '/forms',
          permissions: [Permissions.REPORTS_WRITE],
          subscriptionTierFeature: ['INCENTIVE.ALL'],
        },
        {
          id: 'run-reports',
          title: <FormattedMessage id="run-reports" />,
          type: 'item',
          url: '/reports/run',
          permissions: [Permissions.REPORTS_READ],
        },
      ],
    },
    {
      id: 'forms-group',
      title: <FormattedMessage id="forms-group" />,
      type: 'collapse',
      icon: UploadOutlined,
      permissions: [Permissions.REPORTS_READ],
      subscriptionTierFeature: ['REWARDS.ALL'],
      children: [
        {
          id: 'forms',
          title: <FormattedMessage id="forms" />,
          type: 'item',
          url: '/forms',
          icon: FileTextOutlined,
          permissions: [Permissions.REPORTS_READ],
        },
        {
          id: 'smart-import',
          title: <FormattedMessage id="smart-import" />,
          type: 'item',
          url: '/smart-import',
          icon: ThunderboltOutlined,
          permissions: [Permissions.REWARDS_ADMIN],
        },
      ],
    },
    {
      id: 'configuration',
      title: <FormattedMessage id="configuration" />,
      type: 'collapse',
      icon: SettingOutlined,
      permissions: [Permissions.SETTINGS_WRITE],
      children: [
        {
          id: 'settings',
          title: <FormattedMessage id="settings" />,
          type: 'item',
          url: '/company-settings',
          icon: ToolOutlined,
          permissions: [Permissions.SETTINGS_WRITE],
        },
        {
          id: 'contact-settings',
          title: <FormattedMessage id="contact-settings" />,
          type: 'item',
          url: '/contact-settings',
          icon: ContactsOutlined,
          permissions: [Permissions.SETTINGS_WRITE],
        },
        {
          id: 'integration-settings',
          title: <FormattedMessage id="integration-settings" />,
          type: 'item',
          url: '/integration-settings',
          icon: LockOutlined,
          permissions: [Permissions.SETTINGS_WRITE],
        },
        {
          id: 'admin-ledger',
          title: <FormattedMessage id="admin-ledger" />,
          type: 'item',
          url: '/admin/ledger-accounts',
          icon: ContainerOutlined,
          permissions: [Permissions.LEDGER_ACCOUNTS_WRITE],
          subscriptionTierFeature: ['INCENTIVE.ALL'],
        },

        {
          id: 'wizard',
          title: <FormattedMessage id="wizard" />,
          type: 'item',
          url: '/wizard',
          icon: RocketOutlined,
          permissions: [Permissions.SETTINGS_WRITE],
          subscriptionTierFeature: ['INCENTIVE.ALL'],
          featureFlag: { name: 'wizard', level: 'user' },
        },
      ],
    },
    {
      id: 'customer-support',
      title: <FormattedMessage id="customer-support" />,
      type: 'item',
      url: 'https://support.purcent.com',
      external: true,
      target: true,
      icon: QuestionCircleOutlined,
    },
  ],
}

export default pages
